import {
	AccessCodeChange,
	BarcodeReader,
	Box,
	Device,
	DeviceListener,
	Doorbell,
	EndOccupancyOptions,
	Keypad,
	LocationCodeListener,
	Lock,
	LockStatusListener,
	LogLevel,
	NfcReader,
	Occupancy,
	OccupancyContent,
	OccupyRequest,
	PaymentTerminal,
	SetupUrlListener,
} from "@variocube/app-api";

import {EventSource} from "../EventSource";

export interface Permissions {
	accessLevel: AccessLevel;
}

export enum AccessLevel {
	ReadOnly = "ReadOnly",
	OpenNonOccupied = "OpenNonOccupied",
	FullAccess = "FullAccess",
	MaintenanceOnly = "MaintenanceOnly",
}

export interface SupportInfo {
	operatedBy: string;
	phone: string;
	email: string;
	website: string;
	freetext: string;
}

export interface InternetConnection {
	connected: boolean;
}

export type ConnectionListener = (connected: boolean) => void;

export type StorageListener = (appId: string, key: string) => void;

export type BoxListener = (boxes: Box[]) => void;

export interface OccupancyListener {
	created(occupancy: Occupancy): void;
	updated(occupancy: Occupancy): void;
	accessChanged(occupancy: Occupancy): void;
	ended(uuid: string): void;
}

export type InstalledAppsListener = (apps: string[]) => void;

export type InternetConnectionListener = (connection: InternetConnection) => void;

export type ControllerOccupyRequest = OccupyRequest & {
	appId: string;
};

export interface Controller {
	readonly version: string | undefined;
	readonly hostname: string;
	readonly stage: string;
	readonly region: string;
	readonly locationCode: string;
	readonly port: number;

	readonly nfcReader: NfcReader;
	readonly paymentTerminal: PaymentTerminal;
	readonly barcodeReader: BarcodeReader;
	readonly keypad: Keypad;
	readonly doorbell: Doorbell;

	readonly supportInfo: EventSource<SupportInfo>;

	/**
	 * Requests a value from the controller`s data store
	 * @param appId The app id
	 * @param key The key
	 * @return Promise that resolves to the retrieved value, or undefined if the key is not defined.
	 */
	getStorageItem<T = any>(appId: string, key: string): Promise<T | undefined>;

	/**
	 * Gets the binary value of an item in the controller`s data store
	 * @param appId The app id
	 * @param key The key
	 */
	getStorageItemBlob(appId: string, key: string): Promise<Blob | undefined>;

	/**
	 * Retrieves the status of a lock
	 * @param lock The lock handle
	 * @return Promise that resolves to the lock status
	 */
	getStatus(lock: string): Promise<string>;

	/**
	 * Retrieves the list of locks
	 * @return Promise that resolves to the list of locks
	 */
	getLocks(): Promise<Lock[]>;

	/**
	 * Retrieves the list of boxes
	 * @return Promise that resolves to the list of boxes
	 */
	getBoxes(): Promise<Box[]>;

	/**
	 * Update the box maintenance state
	 * @param boxNumber the box number
	 * @param requiredMaintenance the state whether the maintenance is enabled or disabled
	 */
	updateBoxMaintenanceState(boxNumber: string, requiredMaintenance: boolean): Promise<void>;

	/**
	 * Retrieves the list of devices
	 * @return Promise that resolves to the list of devices
	 */
	getDevices(): Promise<Device[]>;

	/**
	 * Logs the specified message.
	 * @return Promise that resolves if the log entry was successfully created.
	 */
	log(level: LogLevel, source: string, message: string, data: any): Promise<void>;

	/**
	 * Requests to open a lock
	 * @param lock The lock handle
	 * @return Promise that fulfills when the lock was opened
	 */
	open(lock: string): Promise<void>;

	/**
	 * Ends the specified occupancy.
	 * @param occupancyId The occupancy id
	 * @param options Options
	 * @return Promise that resolves when the occupancy was ended.
	 */
	endOccupancy(occupancyId: string, options?: EndOccupancyOptions): Promise<void>;

	/**
	 * Occupies a box according to the specified request.
	 * @param occupyRequest The occupy request
	 * @return Promise that fulfills to a reservation, if it could be made.
	 */
	occupy(occupyRequest: ControllerOccupyRequest): Promise<Occupancy>;

	/**
	 * Confirms the specified occupancy.
	 * @param occupancyId The occupancy id.
	 * @param content The new occupancy content
	 * @param merge Whether the content should be merged upon the existing content.
	 */
	confirmOccupancy(occupancyId: string, content?: OccupancyContent, merge?: boolean): Promise<void>;

	/**
	 * Cancels the specified occupancy.
	 * @param occupancyId The occupancy id.
	 */
	cancelOccupancy(occupancyId: string): Promise<void>;

	/**
	 * Updates the specified occupancy with the specified content
	 * @param occupancyId The occupancy id
	 * @param content The new occupancy content
	 * @param merge Whether the content should be merged upon the existing content.
	 * @return Promise that resolves if the occupancy was successfully updated
	 */
	updateOccupancy(occupancyId: string, content: OccupancyContent, merge: boolean): Promise<void>;

	/**
	 * Changes the access code of an occupancy.
	 * @param occupancyId The occupancy id.
	 * @param accessCode The new access code or a shape to generate one.
	 */
	changeAccessCode(occupancyId: string, accessCode: string | AccessCodeChange): Promise<void>;

	/**
	 * Changes the access keys of an occupancy.
	 * @param occupancyId The occupancy id.
	 * @param accessKeys The new access keys.
	 */
	changeAccessKeys(occupancyId: string, accessKeys: string[]): Promise<void>;

	/**
	 * Retrieves occupancies
	 * @param appId If specified, only occupancies of this app are returned
	 * @param access If specified, only occupancies with the specified access code or key are returned
	 * @return Promise that resolves to a list of matching occupancies.
	 */
	getOccupancies(appId?: string, access?: string): Promise<Occupancy[]>;

	/**
	 * Returns the occupancy with the specified id.
	 * @param occupancyId The id of the reservation
	 * @return Promise that resolves to the reservation or undefined if the reservation was not found.
	 */
	getOccupancy(occupancyId: string): Promise<Occupancy | undefined>;

	/**
	 * Retrieves the list of apps that are configured.
	 */
	getApps(): Promise<string[]>;

	/**
	 * Adds a lock status listener
	 * @param listener
	 */
	addLockStatusListener(listener: LockStatusListener): void;

	/**
	 * Removes a lock status listener
	 * @param listener
	 */
	removeLockStatusListener(listener: LockStatusListener): void;

	/**
	 * Adds a connection listener
	 * @param listener
	 */
	addConnectionListener(listener: ConnectionListener): void;

	/**
	 * Removes a connection listener
	 * @param listener
	 */
	removeConnectionListener(listener: ConnectionListener): void;

	/**
	 * Adds a location code listener
	 * @param listener
	 */
	addLocationCodeListener(listener: LocationCodeListener): void;

	/**
	 * Removes a location code listener
	 * @param listener
	 */
	removeLocationCodeListener(listener: LocationCodeListener): void;

	/**
	 * Adds a setup key listener
	 * @param listener
	 */
	addSetupUrlListener(listener: SetupUrlListener): void;

	/**
	 * Removes a setup key listener
	 * @param listener
	 */
	removeSetupUrlListener(listener: SetupUrlListener): void;

	/**
	 * Adds a storage listener
	 * @param listener
	 */
	addStorageListener(listener: StorageListener): void;

	/**
	 * Removes a storage listener
	 * @param listener
	 */
	removeStorageListener(listener: StorageListener): void;

	/**
	 * Adds an occupancy listener
	 * @param listener
	 */
	addOccupancyListener(listener: OccupancyListener): void;

	/**
	 * Removes an occupancy listener
	 * @param listener
	 */
	removeOccupancyListener(listener: OccupancyListener): void;

	/**
	 * Adds a box listener
	 * @param listener
	 */
	addBoxListener(listener: BoxListener): void;

	/**
	 * Removes a box listener
	 * @param listener
	 */
	removeBoxListener(listener: BoxListener): void;

	/**
	 * Adds a device listener
	 * @param listener
	 */
	addDeviceListener(listener: DeviceListener): void;

	/**
	 * Removes a device listener
	 * @param listener
	 */
	removeDeviceListener(listener: DeviceListener): void;

	/**
	 * Adds an installed apps listener.
	 * @param listener
	 */
	addInstalledAppsListener(listener: InstalledAppsListener): void;

	/**
	 * Removes an installed apps listener.
	 * @param listener
	 */
	removeInstalledAppsListener(listener: InstalledAppsListener): void;

	/**
	 * Adds an internet connection listener.
	 * @param listener
	 */
	addInternetConnectionListener(listener: InternetConnectionListener): void;

	/**
	 * Removes an internet connection listener.
	 * @param listener
	 */
	removeInternetConnectionListener(listener: InternetConnectionListener): void;

	/**
	 * Verifies an authentication code.
	 */
	verifyAuthCode(code: string): Promise<Permissions>;

	/**
	 * Gets  the Support  Info.
	 */
	getSupportInfo(): Promise<SupportInfo>;

	/**
	 * Restarts the controller.
	 */
	restartController(): Promise<void>;

	/**
	 * Restarts the operating system.
	 */
	restartOperatingSystem(): Promise<void>;

	/**
	 * Restarts the user interface.
	 */
	restartUserInterface(): Promise<void>;

	/**
	 * Updates the software to currently defined version.
	 */
	updateSoftware(): Promise<void>;

	/**
	 * Shuts the cube down.
	 */
	shutdown(): Promise<void>;

	/**
	 * Returns information about the internet connection
	 */
	getInternetConnection(): Promise<InternetConnection>;
}
